import { getCSRFHeaders, postJSON } from '@folklore/fetch';
import { useUrlGenerator } from '@folklore/routes';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'wouter';

import * as AppPropTypes from '../../../lib/PropTypes';

import { useGame, useParticipation } from '../../../contexts/GameContext';
import { useTheme, useThemeClassName } from '../../../contexts/ThemeContext';
import StyledButton from '../../buttons/StyledButton';
import PageTitle from '../../partials/PageTitle';

import styles from '../../../../styles/games/exam/questions-group-view.module.scss';

import backButtonImg from '../../../../img/rhoneacademy/icons/back.svg';

const propTypes = {
    questionsGroup: AppPropTypes.questionsGroups,
    reload: PropTypes.func,
};

const defaultProps = {
    questionsGroup: null,
    reload: null,
};

function ExamQuestionGroupView({ questionsGroup, reload }) {
    const { label, questions } = questionsGroup;

    const {
        id: gameId,
        title: gameTitle = null,
        subtitle: gameSubtitle,
        image: gameImage = null,
    } = useGame();
    const { url: gameImageUrl = null } = gameImage || {};

    const [isClearingAnswers, setIsClearingAnswers] = useState(false);
    const participation = useParticipation();
    const { name: themeName } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const [, navigate] = useLocation();
    const url = useUrlGenerator();

    const { formatMessage } = useIntl();
    const learningGuideLabel = defineMessage({
        defaultMessage: 'Learning guide',
        description: 'Rhone Academy - Learning guide CTA',
    });
    const learningGuideLink = defineMessage({
        defaultMessage:
            'https://www.vins-rhone.com/sites/vignoble/files/documentation/2024-12/Kit-pedagogique-2024-EN.pdf',
        description: 'Rhone Academy - Learning guide link',
    });

    const canAnswerQuestions = useMemo(
        () => questions.some(({ validated = null }) => validated !== true),
        [questions],
    );

    const onAnswerQuestionsClicked = useCallback(() => {
        const { id: questionId = null } =
            questions.find(({ validated = null }) => validated !== true) || {};
        if (questionId !== null) {
            navigate(
                url('game.question', {
                    game: gameId,
                    question: questionId,
                }),
            );
        }
    }, [questions, gameId, navigate, url]);

    const onGoToModulesClicked = useCallback(() => navigate(url('play')), [navigate, url]);

    const onClearAnswersClicked = useCallback(() => {
        if (participation === null) {
            return;
        }
        const { id: participationId = null } = participation || {};
        setIsClearingAnswers(true);
        postJSON(
            url('participation.end', { participation: participationId }),
            {},
            {
                credentials: 'include',
                headers: getCSRFHeaders(),
            },
        )
            .then(() => reload())
            .finally(() => setIsClearingAnswers(false));
    }, [url, participation, reload]);

    const formattedLearningGuideLabel = formatMessage(learningGuideLabel);

    return (
        <div className={classNames([styles.container, themeClassName])}>
            <div className={styles.topBar}>
                {themeName === 'rhoneacademy' ? (
                    <button
                        type="button"
                        className={styles.backButton}
                        aria-label={
                            <FormattedMessage
                                defaultMessage="Back"
                                description="Question Group View - Back Button"
                            />
                        }
                        onClick={onGoToModulesClicked}
                    >
                        <img
                            src={backButtonImg}
                            alt={
                                <FormattedMessage
                                    defaultMessage="Back"
                                    description="Question Group View - Back Button"
                                />
                            }
                        />
                    </button>
                ) : null}
            </div>

            <div className={styles.header}>
                {gameTitle !== null ? <h1>{gameTitle}</h1> : null}
                {gameSubtitle !== null ? <h2>{gameSubtitle}</h2> : null}
                {gameImageUrl !== null ? (
                    <img className={styles.image} src={gameImageUrl} alt="Questions group" />
                ) : null}
            </div>

            {themeName === 'rhoneacademy' ? (
                <a
                    className={styles.learningGuideCTA}
                    href={formatMessage(learningGuideLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={formattedLearningGuideLabel}
                >
                    <div>{formattedLearningGuideLabel}</div>
                    <div className={styles.icon}>
                        <FontAwesomeIcon icon={faExternalLink} size="xs" />
                    </div>
                </a>
            ) : null}

            <div className={styles.inner}>
                <PageTitle title={label} className={styles.title} />

                <div className={styles.clearAnswersBtn}>
                    <StyledButton
                        onClick={onClearAnswersClicked}
                        disabled={isClearingAnswers || participation === null}
                    >
                        <FormattedMessage
                            defaultMessage="Clear answers"
                            description="Exam - Clear answers CTA"
                        />
                    </StyledButton>
                </div>

                <div className={styles.questionsGrid}>
                    {questions.map((question) => {
                        const {
                            id: questionId,
                            label: questionLabel = null,
                            points = null,
                            validated = null,
                            valid = null,
                        } = question;
                        return (
                            <div
                                key={questionId}
                                className={classNames(styles.item, {
                                    [styles.unanswered]: validated === false,
                                })}
                            >
                                <div>{questionLabel}</div>
                                {validated !== false ? (
                                    <div
                                        className={classNames(styles.validState, {
                                            [styles.isValid]: valid === true,
                                            [styles.isInvalid]: valid === false,
                                            [styles.isNull]: valid === null,
                                        })}
                                    />
                                ) : null}
                                {points !== null ? (
                                    <div className={styles.points}>{points} pts</div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>

                <div className={styles.playCTA}>
                    {canAnswerQuestions ? (
                        <StyledButton onClick={onAnswerQuestionsClicked}>
                            <FormattedMessage
                                defaultMessage="Answer questions"
                                description="Exam - Play CTA"
                            />
                        </StyledButton>
                    ) : (
                        <StyledButton onClick={onGoToModulesClicked}>
                            <FormattedMessage
                                defaultMessage="Go back to modules"
                                description="Exam - Play CTA"
                            />
                        </StyledButton>
                    )}
                </div>
            </div>
        </div>
    );
}

ExamQuestionGroupView.propTypes = propTypes;
ExamQuestionGroupView.defaultProps = defaultProps;

export default ExamQuestionGroupView;
